import { Message } from "src/modules/communication-protobuf";
import { electronOnly } from "src/utils/environment";
import debug from "src/modules/debug-logger";
import RtcManager from "src/modules/rtc/RtcManager";
import { Activities } from "src/modules/activity-log/activity";
import createOpusRecorder from "src/modules/audio-processing/createOpusRecorder";
import createOpusPlayer from "src/modules/audio-processing/createOpusPlayer";
import { getVideoStream } from "src/modules/rtc/video-stream";

const remote = window.require("@electron/remote");
const log = window.require("electron-log");

export default class BabyStationRtcManager extends RtcManager {
  constructor({ messenger, onRecreateConnection }) {
    super({ messenger, isPolite: true });
    this.onRecreateConnection = onRecreateConnection;
    this.messenger.on(Message.WebRTCCandidate.name, this.handleWebRTCCandidates);
    this.messenger.on(Message.WebRTCCreateOffer.name, this.handleOffer);
    this.messenger.on(Message.WebRTCCreateAnswer.name, this.handleAnswer);
    this.messenger.on(Message.AudioPacket.name, this.onAudioPacket);
    this.on("connection-state-change", this.checkPeerConnection);
    this.on("signaling-state-change", this.checkPeerConnection);
    electronOnly(() => {
      remote.powerMonitor.on("resume", this.onResume);
      remote.powerMonitor.on("suspend", this.onSuspend);
    });
    this.activity = Activities.SLEEPING;
    this.peersWithEveryNoise = {};
  }

  async initialize({ hasCameraPermission }) {
    this.opusRecorder = await createOpusRecorder({
      onFlush: this.handleAudioDataFlush
    });

    if (hasCameraPermission) {
      try {
        this.videoStream = await getVideoStream();
        this.videoTrack = this.videoStream.getVideoTracks()[0];
        // fix for browser not allowing audioContext
        this.opusRecorder.resume();
      } catch (error) {
        log.error("cannot create videoStream");
        this.videoStream = null;
      }
    }
  }

  async checkPeerConnection(peerJid) {
    const parent = this.peers[peerJid];
    if (parent == null || parent.peerConnection == null) return;
    const { connectionState, signalingState } = parent.peerConnection;
    if (connectionState === "disconnected" || signalingState === "closed") {
      // stop video receiving
      debug.babyStation(
        `closing and recreating peerConnection, connectionState === ${connectionState}, signalingState=${signalingState}`,
        peerJid
      );
      parent.peerConnection.close();
      this.onRecreateConnection(peerJid);
      this.recreateConnection({ peerJid, delayVideoReconnect: false });
    }
  }

  onSuspend = () => {
    // stop video receiving
    this.off("connection-state-change", this.checkPeerConnection);
    this.off("signaling-state-change", this.checkPeerConnection);
    Object.values(this.peers).forEach((peer) => {
      this.onRecreateConnection(peer.jid);
      if (peer.peerConnection) peer.peerConnection.close();
    });
  };

  onResume = () => {
    debug.babyStation(`powerMonitor.on("resume") fired, closing and recreating all peerConnections`);

    Object.values(this.peers).forEach((peer) => {
      this.recreateConnection({
        peerJid: peer.jid,
        delayVideoReconnect: false
      });
    });

    this.on("connection-state-change", this.checkPeerConnection);
    this.on("signaling-state-change", this.checkPeerConnection);
  };

  initializeParent(parentJid, parentState) {
    debug.rtcManager("Initializing new parent, jid:", parentJid);
    const parent = this.initializePeer(parentJid, parentState);
    return parent;
  }

  onAudioPacket = async ({ data, listening, flush, codec }, from) => {
    let parent = this.getPeer(from);

    if (parent == null || parent.creatingPlayer) return;
    if (parent.opusPlayer == null) {
      parent.creatingPlayer = true;
      parent.opusPlayer = await createOpusPlayer();
      parent.creatingPlayer = false;
      return;
    }
    parent.opusPlayer.onData(data);
  };

  handleAudioDataFlush = (buffer) => {
    const { peers, peersWithEveryNoise } = Object.values(this.peers).reduce(
      (all, one) => {
        if (this.peersWithEveryNoise[one.jid]) all.peersWithEveryNoise.push(one);
        else all.peers.push(one);
        return all;
      },
      {
        peers: [],
        peersWithEveryNoise: []
      }
    );
    if (peers.length === 0 && peersWithEveryNoise.length === 0) return;
    const message = Message.AudioPacket.create({
      codec: "OPUS",
      data: buffer
    });

    const send = (peer) => {
      const { jid, peerConnection } = peer;
      if (!peerConnection) return;
      this.messenger.sendAsEnvelope({
        to: jid,
        payload: { message }
      });
    };

    peersWithEveryNoise.forEach(send);
    if (this.activity === Activities.AWAKE) {
      this.emit("local-audio-data", buffer);
      peers.forEach(send);
    }
  };

  removeParent(jid) {
    debug.rtcManager("about to remove parent, jid", jid);
    if (this.peers[jid] == null) {
      return;
    }
    const parent = this.peers[jid];
    parent.receiveBuffer && parent.receiveBuffer.clear();
    parent.audioPlayer && parent.audioPlayer.close();
    this.destroyPeerConnection(jid);
  }

  removeListeners = () => {
    debug.rtcManager("about to remove event listeners");
    const { messenger } = this;
    messenger.removeListener(Message.AudioPacket.name, this.onAudioPacket);
    messenger.removeListener(Message.WebRTCCreateOffer.name, this.handleOffer);
    messenger.removeListener(Message.WebRTCCreateAnswer.name, this.handleAnswer);
    messenger.removeListener(Message.WebRTCCandidate.name, this.handleWebRTCCandidates);
  };
  destroy() {
    this._destroy();
  }
}
